<template>
  <v-app>
    <v-main class="login-background">
      <v-container fluid class="fill-height">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="5" lg="4" xl="3">
            <!-- Card de Login -->
            <v-card class="login-card pa-8" elevation="8">
              <!-- Logo y Título -->
              <div class="text-center mb-6">
                <div class="mb-4 d-flex justify-center">
                  <FacturacionLogo :size="80" />
                </div>
                <h1 class="text-h5 font-weight-bold text-primary">
                  Sistema DTE
                </h1>
                <p class="text-body-2 text-grey mt-1">
                  Facturación Electrónica - El Salvador
                </p>
              </div>

              <!-- Selector de Ambiente -->
              <v-select
                v-model="credentials.ambiente"
                :items="ambientesOptions"
                item-title="text"
                item-value="value"
                label="Ambiente"
                prepend-inner-icon="mdi-server"
                class="mb-4"
              >
                <template v-slot:selection="{ item }">
                  <v-chip
                    :color="item.raw.value === '00' ? 'warning' : 'success'"
                    size="small"
                    label
                  >
                    <v-icon start size="16">
                      {{ item.raw.value === '00' ? 'mdi-test-tube' : 'mdi-check-circle' }}
                    </v-icon>
                    {{ item.raw.text }}
                  </v-chip>
                </template>
              </v-select>

              <!-- Mensaje de Error -->
              <v-alert
                v-if="errorMessage"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-4"
                closable
                @click:close="errorMessage = ''"
              >
                {{ errorMessage }}
              </v-alert>

              <!-- Botón de Google Sign-In -->
              <v-btn
                color="primary"
                size="x-large"
                block
                :loading="googleLoading"
                @click="handleGoogleLogin"
                class="mb-4 google-btn"
              >
                <template v-slot:prepend>
                  <svg class="google-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </template>
                {{ googleLoading ? 'Validando credenciales...' : 'Iniciar sesión con Google' }}
              </v-btn>

              <!-- Footer -->
              <div class="text-center">
                <p class="text-caption text-medium-emphasis">
                  Sistema de Facturación Electrónica
                </p>
                <p class="text-caption text-medium-emphasis">
                  Ministerio de Hacienda - El Salvador
                </p>
              </div>
            </v-card>

            <!-- Información adicional -->
            <div class="text-center mt-4">
              <v-chip
                :color="credentials.ambiente === '00' ? 'warning' : 'success'"
                variant="flat"
                size="small"
              >
                <v-icon start size="14">
                  {{ credentials.ambiente === '00' ? 'mdi-alert-circle' : 'mdi-shield-check' }}
                </v-icon>
                {{ credentials.ambiente === '00' ? 'Modo de Pruebas' : 'Ambiente Productivo' }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Modal de Selección de Sucursal -->
    <v-dialog v-model="modalSucursal" max-width="500" persistent>
      <v-card class="pa-4">
        <v-card-title class="text-center">
          <v-icon size="48" color="primary" class="mb-2">mdi-store</v-icon>
          <h2 class="text-h5 font-weight-bold">Seleccionar Sucursal</h2>
          <p class="text-body-2 text-grey mt-1">
            Elija la sucursal en la que trabajará durante esta sesión
          </p>
        </v-card-title>

        <v-card-text>
          <v-list lines="two" class="sucursal-list">
            <v-list-item
              v-for="establecimiento in establecimientosUsuario"
              :key="establecimiento.id"
              :value="establecimiento.id"
              @click="seleccionarSucursal(establecimiento)"
              class="sucursal-item mb-2"
              rounded="lg"
              :class="{ 'sucursal-selected': sucursalSeleccionada?.id === establecimiento.id }"
            >
              <template v-slot:prepend>
                <v-avatar :color="establecimiento.tipoEstablecimiento === '01' ? 'primary' : 'secondary'" size="48">
                  <v-icon color="white">
                    {{ establecimiento.tipoEstablecimiento === '01' ? 'mdi-domain' : 'mdi-store' }}
                  </v-icon>
                </v-avatar>
              </template>

              <v-list-item-title class="font-weight-bold">
                {{ establecimiento.nombre }}
              </v-list-item-title>
              <v-list-item-subtitle>
                <v-chip size="x-small" :color="establecimiento.tipoEstablecimiento === '01' ? 'primary' : 'secondary'" variant="tonal" class="mr-1">
                  {{ establecimiento.tipoEstablecimientoDesc }}
                </v-chip>
                {{ establecimiento.municipioDesc }}, {{ establecimiento.departamentoDesc }}
              </v-list-item-subtitle>

              <template v-slot:append>
                <v-icon v-if="sucursalSeleccionada?.id === establecimiento.id" color="success">
                  mdi-check-circle
                </v-icon>
              </template>
            </v-list-item>
          </v-list>

          <v-alert
            v-if="establecimientosUsuario.length === 0"
            type="warning"
            variant="tonal"
            class="mt-4"
          >
            No tiene sucursales asignadas. Contacte al administrador.
          </v-alert>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn variant="outlined" @click="cancelarSeleccion">
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            size="large"
            :disabled="!sucursalSeleccionada"
            @click="confirmarSucursal"
          >
            <v-icon start>mdi-check</v-icon>
            Continuar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import emisoresService from '@/services/emisores.service'
import FacturacionLogo from '@/components/brand/FacturacionLogo.vue'

// Google Client ID desde variables de entorno
const GOOGLE_CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID

export default {
  name: 'LoginView',
  components: { FacturacionLogo },

  setup() {
    const router = useRouter()
    const route = useRoute()

    // Redirigir a la página donde estaba si la sesión expiró
    const redirigirPostLogin = () => {
      const redirectPath = route.query.redirect
      if (redirectPath && redirectPath !== '/login') {
        router.push(redirectPath)
      } else {
        router.push({ name: 'dashboard' })
      }
    }
    const store = useStore()

    // Estado del formulario
    const loginForm = ref(null)
    const formValid = ref(false)
    const loading = ref(false)
    const showPassword = ref(false)
    const errorMessage = ref('')

    // Logo del sistema
    const systemLogoUrl = ref(null)

    // Estado para Google Sign-In
    const googleLoading = ref(false)
    const googleButtonContainer = ref(null)
    let googleClient = null

    // Modal de sucursal
    const modalSucursal = ref(false)
    const establecimientosUsuario = ref([])
    const sucursalSeleccionada = ref(null)
    const usuarioAutenticado = ref(null)

    // Credenciales
    const credentials = reactive({
      usuario: '',
      password: '',
      ambiente: '01' // Por defecto: Productivo
    })

    // Ambiente por defecto del login: (1) el que venga en ?redirect (SmartLab abre el wizard con
    // ?ambiente=00|01 y, si no hay sesion, Smartix cae a /login?redirect=...ambiente=00); (2) si no,
    // por host: UAT/demo -> Pruebas. Evita que en UAT el selector arranque en "Productivo" y se emita
    // una factura productiva por error. En produccion queda '01'.
    const ambienteInicial = () => {
      try {
        const r = String(route.query.redirect || '')
        const m = r.match(/[?&]ambiente=(00|01)/)
        if (m) return m[1]
      } catch (e) { /* noop */ }
      try {
        const host = (window.location.host || '').toLowerCase()
        if (host.includes('uat') || host.includes('demo')) return '00'
      } catch (e) { /* noop */ }
      return '01'
    }
    credentials.ambiente = ambienteInicial()

    // Opciones de ambiente
    const ambientesOptions = [
      { value: '00', text: 'Ambiente de Pruebas' },
      { value: '01', text: 'Ambiente Productivo' }
    ]

    // Reglas de validación
    const emailRules = [
      v => !!v || 'El correo electrónico es requerido',
      v => /.+@.+\..+/.test(v) || 'Ingrese un correo válido'
    ]

    const passwordRules = [
      v => !!v || 'La contraseña es requerida',
      v => (v && v.length >= 4) || 'Mínimo 4 caracteres'
    ]

    // Función de login
    const handleLogin = async () => {
      if (!formValid.value) return

      loading.value = true
      errorMessage.value = ''

      try {
        // Llamar al backend para autenticación
        const result = await store.dispatch('auth/login', {
          usuario: credentials.usuario,
          password: credentials.password,
          ambiente: credentials.ambiente
        })

        if (result.success) {
          usuarioAutenticado.value = result.user
          // Cargar datos iniciales (emisor, catálogos, documentos, establecimientos)
          await store.dispatch('initApp')
          // Login exitoso - redirigir a donde estaba o al dashboard
          redirigirPostLogin()
        } else {
          errorMessage.value = result.message || 'Credenciales incorrectas'
        }
      } catch (error) {
        errorMessage.value = 'Error de conexión. Intente nuevamente.'
      } finally {
        loading.value = false
      }
    }

    // Seleccionar sucursal
    const seleccionarSucursal = (establecimiento) => {
      sucursalSeleccionada.value = establecimiento
    }

    // Confirmar sucursal y continuar
    const confirmarSucursal = async () => {
      if (!sucursalSeleccionada.value) return

      try {
        await store.dispatch('emisor/fetchEstablecimientos')
        await store.dispatch('emisor/selectEstablecimiento', sucursalSeleccionada.value.id)
        modalSucursal.value = false
        redirigirPostLogin()
      } catch (error) {
        errorMessage.value = 'Error al seleccionar la sucursal. Intente nuevamente.'
      }
    }

    // Cancelar selección y cerrar sesión
    const cancelarSeleccion = async () => {
      modalSucursal.value = false
      await store.dispatch('auth/logout')
      sucursalSeleccionada.value = null
      establecimientosUsuario.value = []
    }

    // =========================================================================
    // GOOGLE SIGN-IN
    // =========================================================================

    // Cargar el script de Google Identity Services
    const loadGoogleScript = () => {
      return new Promise((resolve, reject) => {
        // Si ya está cargado, resolver inmediatamente
        if (window.google?.accounts?.oauth2) {
          resolve()
          return
        }

        // Verificar si el script ya existe
        const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]')
        if (existingScript) {
          existingScript.addEventListener('load', resolve)
          existingScript.addEventListener('error', reject)
          return
        }

        // Crear y cargar el script
        const script = document.createElement('script')
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.defer = true
        script.referrerPolicy = 'origin'
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
    }

    // Inicializar Google OAuth2 Token Client (access token directo via popup)
    const initGoogleClient = async () => {
      try {
        await loadGoogleScript()

        if (!GOOGLE_CLIENT_ID) {
          return
        }

        googleClient = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'email profile',
          callback: handleGoogleCallback
        })
      } catch (error) {
        // Error al cargar Google Identity Services
      }
    }

    // Manejar el click en el botón de Google
    const handleGoogleLogin = async () => {
      if (!googleClient) {
        await initGoogleClient()
      }

      if (googleClient) {
        googleLoading.value = true
        errorMessage.value = ''
        googleClient.requestAccessToken()
        // Si el usuario cierra el popup sin completar, Google no siempre dispara el callback.
        // Escuchamos el re-focus de la ventana para resetear el botón tras un breve delay.
        const onWindowFocus = () => {
          setTimeout(() => {
            if (googleLoading.value) {
              googleLoading.value = false
            }
          }, 1500)
          window.removeEventListener('focus', onWindowFocus)
        }
        window.addEventListener('focus', onWindowFocus)
      } else {
        errorMessage.value = 'No se pudo inicializar Google Sign-In. Intente recargar la página.'
      }
    }

    // Callback cuando Google responde con el access token
    const handleGoogleCallback = async (response) => {
      try {
        if (response.error) {
          googleLoading.value = false
          if (response.error !== 'access_denied') {
            errorMessage.value = 'Error al autenticar con Google'
          }
          return
        }

        const accessToken = response.access_token

        if (!accessToken) {
          googleLoading.value = false
          errorMessage.value = 'No se recibió el token de Google'
          return
        }

        // Llamar al backend con el access token
        const result = await store.dispatch('auth/googleLogin', {
          accessToken,
          ambiente: credentials.ambiente
        })

        googleLoading.value = false

        if (result.success) {
          usuarioAutenticado.value = result.user
          await store.dispatch('initApp')
          redirigirPostLogin()
        } else {
          errorMessage.value = result.message || 'Error al iniciar sesión con Google'
        }
      } catch (error) {
        googleLoading.value = false
        errorMessage.value = 'Error al procesar la autenticación de Google'
      }
    }

    // Detectar autofill del navegador y forzar validación + inicializar Google
    onMounted(async () => {
      // Pequeño delay para que el navegador complete el autofill
      setTimeout(() => {
        const emailInput = document.querySelector('input[type="email"]')
        const passwordInput = document.querySelector('input[type="password"]')

        // Verificar si los campos tienen valores (autofill)
        if (emailInput && emailInput.value) {
          credentials.usuario = emailInput.value
        }
        if (passwordInput && passwordInput.value) {
          credentials.password = passwordInput.value
        }

        // Forzar validación del formulario si hay valores
        if (credentials.usuario || credentials.password) {
          loginForm.value?.validate()
        }
      }, 100)

      // Inicializar Google Sign-In
      initGoogleClient()

      // Cargar logo del sistema
      try {
        const response = await emisoresService.getSystemLogo()
        if (response?.url) {
          systemLogoUrl.value = `${response.url}?t=${Date.now()}`
        }
      } catch {
        // Sin logo configurado, se muestra el ícono por defecto
      }
    })

    // Limpiar al desmontar
    onUnmounted(() => {
      googleClient = null
    })

    return {
      loginForm,
      formValid,
      loading,
      showPassword,
      errorMessage,
      credentials,
      ambientesOptions,
      emailRules,
      passwordRules,
      handleLogin,
      modalSucursal,
      establecimientosUsuario,
      sucursalSeleccionada,
      seleccionarSucursal,
      confirmarSucursal,
      cancelarSeleccion,
      // Google Sign-In
      googleLoading,
      handleGoogleLogin,
      // Logo del sistema
      systemLogoUrl
    }
  }
}
</script>

<style scoped>
.login-background {
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgba(var(--v-theme-primary), 0.85) 50%, rgba(var(--v-theme-primary), 0.7) 100%);
  min-height: 100vh;
}

.login-card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
}

.v-text-field {
  margin-bottom: 8px;
}

/* Animación sutil al cargar */
.login-card {
  animation: slideUp 0.4s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Estilos para la lista de sucursales */
.sucursal-list {
  max-height: 350px;
  overflow-y: auto;
}

.sucursal-item {
  border: 1px solid #E5E7EB;
  transition: all 0.2s ease;
  cursor: pointer;
}

.sucursal-item:hover {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.sucursal-selected {
  border-color: rgb(var(--v-theme-primary)) !important;
  background-color: rgba(var(--v-theme-primary), 0.08) !important;
}

/* ============================================
   FIX COMPLETO PARA VISIBILIDAD EN LOGIN
   ============================================ */

/* Forzar texto oscuro en TODOS los estados del input */
.login-card :deep(.v-field input) {
  color: #1a1a1a !important;
  -webkit-text-fill-color: #1a1a1a !important;
  caret-color: #1a1a1a !important;
  background-color: white !important;
}

/* Forzar fondo blanco en el campo */
.login-card :deep(.v-field__field) {
  background-color: white !important;
}

/* Asegurar que los iconos de los inputs sean visibles */
.login-card :deep(.v-field__prepend-inner .v-icon),
.login-card :deep(.v-field__append-inner .v-icon) {
  color: rgba(0, 0, 0, 0.54) !important;
  opacity: 1 !important;
}

/* Labels de los inputs - estado normal (placeholder) */
.login-card :deep(.v-field-label:not(.v-field-label--floating)) {
  color: rgba(0, 0, 0, 0.6) !important;
  opacity: 1 !important;
  background-color: transparent !important;
}

/* Labels flotantes (cuando el campo tiene valor) */
.login-card :deep(.v-field-label--floating) {
  color: white !important;
  -webkit-text-fill-color: white !important;
  padding: 0 4px !important;
  opacity: 1 !important;
  text-shadow:
    -1px -1px 0 #666,
    1px -1px 0 #666,
    -1px 1px 0 #666,
    1px 1px 0 #666,
    0 0 3px rgba(0,0,0,0.5) !important;
}

/* Labels del v-select */
.login-card :deep(.v-select .v-field-label) {
  color: rgba(0, 0, 0, 0.6) !important;
  background-color: white !important;
  padding: 0 4px !important;
}

/* Fix para autofill del navegador */
.login-card :deep(.v-field input:-webkit-autofill),
.login-card :deep(.v-field input:-webkit-autofill:hover),
.login-card :deep(.v-field input:-webkit-autofill:focus),
.login-card :deep(.v-field input:-webkit-autofill:active) {
  -webkit-box-shadow: 0 0 0 9999px white inset !important;
  -webkit-text-fill-color: #1a1a1a !important;
  caret-color: #1a1a1a !important;
  transition: background-color 9999s ease-out, color 9999s ease-out !important;
}

/* Forzar que el label suba cuando hay autofill */
.login-card :deep(.v-field:has(input:-webkit-autofill)) .v-field-label {
  transform: translateY(-50%) scale(0.75) !important;
  top: 0 !important;
  background: white !important;
  padding: 0 4px !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

/* Logo avatar con ring */
.login-card .v-avatar {
  box-shadow: 0 0 0 4px rgba(var(--v-theme-primary), 0.15);
  transition: transform 0.3s ease;
}

.login-card .v-avatar:hover {
  transform: scale(1.05);
}

/* Estilos para el botón de Google */
.google-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0;
  color: white !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease !important;
}

.google-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.4) !important;
}

.google-btn:active {
  transform: translateY(0);
}

.google-icon {
  margin-right: 8px;
}

/* Forzar textos oscuros dentro del login card (siempre tiene fondo blanco) */
.login-card .text-h5 {
  color: rgb(var(--v-theme-primary)) !important;
}

.login-card .text-body-2.text-grey,
.login-card .text-caption.text-medium-emphasis {
  color: rgba(0, 0, 0, 0.6) !important;
}

.login-card .v-divider {
  border-color: rgba(0, 0, 0, 0.12) !important;
}
</style>
